import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../../components/Layout';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Search, DollarSign, TrendingDown, Landmark, Users, Download } from 'lucide-react';
import { toast } from 'sonner';
import { formatCurrency, formatPercent } from '../../lib/formatters';
import { Button } from '../../components/ui/button';
import OutstandingTiles from '../../components/syndication/OutstandingTiles';   // 10/23: what is outstanding
import OffersInFlight from '../../components/syndication/OffersInFlight';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SyndicationCapitalDashboard() {
  const { getAuthHeaders } = useAuth();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(true);

  const fetchSummary = useCallback(async () => {
    setSummaryLoading(true);
    try {
      const r = await axios.get(`${API}/syndications-outstanding/summary`, { headers: { ...getAuthHeaders(), 'X-Tenant': 'live' } });
      setSummary(r.data);
    } catch (err) {
      toast.error('Failed to load the outstanding summary');
    } finally { setSummaryLoading(false); }
  }, [getAuthHeaders]);
  useEffect(() => { fetchSummary(); }, [fetchSummary]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (typeFilter) params.append('type_filter', typeFilter);

      const res = await axios.get(`${API}/syndications/capital-dashboard?${params}`, {
        headers: { ...getAuthHeaders(), 'X-Tenant': 'live' },
      });
      setData(res.data);
    } catch (err) {
      console.error(err);
      toast.error('Failed to load capital dashboard');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders, search, typeFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleExport = async () => {
    try {
      const res = await axios.get(`${API}/syndications/export`, {
        headers: { ...getAuthHeaders(), 'X-Tenant': 'live' },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      const disposition = res.headers['content-disposition'] || '';
      const match = disposition.match(/filename=(.+)/);
      link.setAttribute('download', match ? match[1] : 'syndication_export.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Export downloaded');
    } catch (err) {
      toast.error('Failed to export');
    }
  };

  // Summary totals
  const totals = data.reduce(
    (acc, r) => ({
      funded: acc.funded + r.total_funded,
      feeCredit: acc.feeCredit + r.total_fee_credit,
      adjustedCapital: acc.adjustedCapital + r.adjusted_capital,
      netOwed: acc.netOwed + r.net_owed,
    }),
    { funded: 0, feeCredit: 0, adjustedCapital: 0, netOwed: 0 }
  );

  return (
    <Layout>
      <div className="space-y-6" data-testid="capital-dashboard-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Syndications Dashboard</h1>
            <p className="text-sm text-slate-500 mt-1">What is outstanding — each box is a shortcut. Capital overview by syndicator below.</p>
          </div>
          <Button variant="outline" size="sm" onClick={handleExport} data-testid="export-btn">
            <Download className="h-4 w-4 mr-1.5" /> Export to Excel
          </Button>
        </div>

        {/* 10/23 (Josh): outstanding boxes like the home page + offers out for confirmation (send / resend) */}
        <OutstandingTiles summary={summary} loading={summaryLoading} />
        <OffersInFlight onChanged={fetchSummary} />

        <h2 className="text-lg font-semibold text-slate-800 pt-2">Capital overview by syndicator</h2>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-slate-500">Total Funded</CardTitle>
              <DollarSign className="h-4 w-4 text-slate-400" />
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold" data-testid="total-funded">{formatCurrency(totals.funded)}</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-slate-500">Total Fee Credit</CardTitle>
              <TrendingDown className="h-4 w-4 text-slate-400" />
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold" data-testid="total-fee-credit">{formatCurrency(totals.feeCredit)}</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-slate-500">Adjusted Capital</CardTitle>
              <Landmark className="h-4 w-4 text-slate-400" />
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold" data-testid="total-adjusted-capital">{formatCurrency(totals.adjustedCapital)}</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-slate-500">Net Owed</CardTitle>
              <Users className="h-4 w-4 text-slate-400" />
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold text-amber-600" data-testid="total-net-owed">{formatCurrency(totals.netOwed)}</p>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[200px] max-w-xs">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              placeholder="Search syndicator..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
              data-testid="capital-dashboard-search"
            />
          </div>
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="h-10 rounded-md border border-slate-200 bg-white px-3 text-sm"
            data-testid="capital-dashboard-type-filter"
          >
            <option value="">All Types</option>
            <option value="Partner">Partner</option>
            <option value="Syndicator">Syndicator</option>
          </select>
        </div>

        {/* Table */}
        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
              </div>
            ) : data.length === 0 ? (
              <div className="text-center py-12 text-slate-500">No syndication data found</div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Syndicator</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead className="text-right">Deals</TableHead>
                      <TableHead className="text-right">Total Funded</TableHead>
                      <TableHead className="text-right">Principal Returned</TableHead>
                      <TableHead className="text-right">Fee Credit</TableHead>
                      <TableHead className="text-right">Adjusted Capital</TableHead>
                      <TableHead className="text-right">Net Owed</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.map((row) => (
                      <TableRow key={row.syndicator_id} data-testid={`capital-row-${row.syndicator_id}`}>
                        <TableCell className="font-medium">
                          <Link to={`/syndications/syndicator/${row.syndicator_id}`} className="text-blue-600 hover:underline" data-testid={`capital-syndicator-link-${row.syndicator_id}`}>
                            {row.syndicator_name}
                          </Link>
                        </TableCell>
                        <TableCell>
                          <Badge variant={row.type === 'Partner' ? 'default' : 'secondary'}>
                            {row.type}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">{row.deal_count}</TableCell>
                        <TableCell className="text-right">{formatCurrency(row.total_funded)}</TableCell>
                        <TableCell className="text-right">{formatCurrency(row.total_principal_returned)}</TableCell>
                        <TableCell className="text-right">{formatCurrency(row.total_fee_credit)}</TableCell>
                        <TableCell className="text-right">{formatCurrency(row.adjusted_capital)}</TableCell>
                        <TableCell className={`text-right font-semibold ${row.net_owed > 0 ? 'text-amber-600' : ''}`}>
                          {formatCurrency(row.net_owed)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
